import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingCart, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { usePrimaryProduct } from "../context/ProductContext";

const NavBar: React.FC = () => {
  const { totalItems, openCart } = useCart();
  const { primaryProductId } = usePrimaryProduct();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const shopLink = primaryProductId ? `/products/${primaryProductId}` : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const isHome = location.pathname === "/";
  const isShop = location.pathname.startsWith("/products");

  return (
    <>
      {/* Main nav bar */}
      <header
        className={`fixed left-0 right-0 z-40 transition-all duration-300 ${
          scrolled || !isHome
            ? "bg-ks-bg/95 backdrop-blur-md border-b border-ks-border"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img
              src="/logo.png"
              alt="KitStudio"
              className="h-8 w-auto opacity-90 group-hover:opacity-100 transition-opacity"
            />
          </Link>

          {/* Desktop nav links */}
          <nav className="hidden md:flex items-center gap-8">
            <Link
              to={shopLink}
              className={`font-saira uppercase tracking-widest text-sm transition-colors ${
                isShop ? "text-ks-text" : "text-ks-secondary hover:text-ks-text"
              }`}
            >
              Shop
            </Link>
          </nav>

          {/* Right icons */}
          <div className="flex items-center gap-4">
            {/* Cart */}
            <button
              onClick={openCart}
              className="relative text-ks-secondary hover:text-ks-text transition-colors"
              aria-label="Open cart"
            >
              <ShoppingCart size={22} strokeWidth={1.5} />
              {totalItems > 0 && (
                <span className="absolute -top-2 -right-2 bg-ks-text text-ks-bg text-[10px] font-montserrat font-bold w-4 h-4 rounded-full flex items-center justify-center leading-none">
                  {totalItems > 9 ? "9+" : totalItems}
                </span>
              )}
            </button>

            {/* Mobile hamburger */}
            <button
              className="md:hidden text-ks-secondary hover:text-ks-text transition-colors"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {menuOpen ? (
                <X size={22} strokeWidth={1.5} />
              ) : (
                <Menu size={22} strokeWidth={1.5} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-ks-surface border-t border-ks-border px-4 py-6 flex flex-col gap-6 animate-fade-in">
            <Link
              to={shopLink}
              className="font-saira uppercase tracking-widest text-lg text-ks-text"
            >
              Shop
            </Link>
          </div>
        )}
      </header>

      {/* Spacer so content starts below fixed nav */}
      <div style={{ height: "calc(40px + 64px)" }} />
    </>
  );
};

export default NavBar;
